import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, throwError, of, timer } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SenseSphereOAuthService } from './sensesphere-oauth.service';

const CHATBOT_API_BASE = environment.chatbot.baseUrl;
const CHATBOT_STORAGE_PREFIX = 'chatbot';
const CHATBOT_USER_ID_KEY = `${CHATBOT_STORAGE_PREFIX}:userId`;
const CHATBOT_FINGERPRINT_KEY = `${CHATBOT_STORAGE_PREFIX}:fingerprint`;
const CHATBOT_SESSION_KEY_SUFFIX = 'session';
// Anonymous identity issued by the backend. Its httpOnly cookie is never sent
// cross-origin, so the id must be persisted and sent back in each request body.
const CHATBOT_DEVICE_ID_KEY = `${CHATBOT_STORAGE_PREFIX}:deviceId`;

function generateUserId(): string {
    return 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

function generateFingerprint(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

export interface MessageFeedback {
    rating: number;
    liked: boolean;
    submitted_at?: string;
}

export interface ChatMessage {
    id: string;
    order?: number;
    agent: 'user' | 'bot';
    message: string;
    bot?: number;
    bot_name?: string;
    bot_avatar?: string;
    image_url?: string | null;
    created_at: string;
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
    cached_tokens?: number;
    user_rating?: 'up' | 'down' | null;
    lms_config_name?: string;
    status?: 'sending' | 'sent' | 'failed';
    user_feedback?: MessageFeedback;
}

export interface ChatSession {
    session_uuid: string;
    session_id: number;
    api_session_id?: string;
    bot_name: string;
    existing: boolean;
    messages: ChatMessage[];
    message_count: number;
    project_id?: number;
    is_archived?: boolean;
    fingerprint?: string;
    info_needed?: boolean;
    welcome_message?: string | null;
    user_info?: { name: string; gender?: string | null; information?: string | null };
}

export interface ChatResponse {
    success: boolean;
    response: string;
    response_html?: string;
    user_message_id: string;
    bot_message_id: string;
    timestamp: string;
    session_title?: string | null;
    total_tokens?: number;
    prompt_tokens?: number;
    completion_tokens?: number;
    cached_tokens?: number;
    new_session?: boolean;
}

export interface FeedbackResponse {
    success: boolean;
    message_id: string;
    user_rating: number;
    bonus_messages_earned: number;
}

export interface AssistantQuestion {
    botUuid: string;
    question: string;
    createdAt: number;
}

// A forwarded question is dropped if no chatbot picks it up within this time
const PENDING_QUESTION_TTL_MS = 60 * 1000;

// How long to keep polling a chat request the backend reported as still pending
const CHAT_REQUEST_WAIT_MS = 60 * 1000;
const CHAT_REQUEST_SLOW_MESSAGE = 'Trợ lý đang phản hồi chậm. Vui lòng thử lại sau ít phút.';
const CHAT_REQUEST_FAILED_MESSAGE = 'Chưa nhận được phản hồi từ trợ lý. Vui lòng thử lại sau.';

export interface IcdCodeInfo {
    path: string;
    model: string;
    id: string;
}

/**
 * Convert ICD code to database id format
 * Disease: A00.0 -> A000 (remove decimal)
 * Type: A00 -> A00 (no change)
 * Section: A00-A09 -> A00-A09 (no change)
 * Chapter: I -> I (no change)
 */
function convertCodeToId(code: string): string {
    // Disease format: A00.0, A00.00 - remove the decimal point
    if (/^[A-Z]\d{2}\.\d+$/.test(code)) {
        return code.replace('.', '');
    }
    // Type, Section, Chapter - use as-is
    return code;
}

export interface ProcessedMessage {
    text: string;
    codes: IcdCodeInfo[];
}

@Injectable({
    providedIn: 'root',
})
export class ChatbotService {
    private _botUuid: string = '';
    private currentSessionUuid: string | null = null;
    // Identity the stored session belongs to: SenseSphere user uuid, or null when anonymous
    private currentSessionOwner: string | null = null;

    set botUuid(value: string) {
        if (value === this._botUuid) {
            return; // Avoid redundant reload
        }
        this._botUuid = value;
        this.currentSessionUuid = null;
        this.currentSessionOwner = null;
        this.sessionSubject.next(null);
        this.loadSessionFromStorage();
    }

    get botUuid(): string {
        return this._botUuid;
    }

    private sessionSubject = new BehaviorSubject<ChatSession | null>(null);
    public session$ = this.sessionSubject.asObservable();

    private readonly userId: string = this.getOrCreateStorageValue(CHATBOT_USER_ID_KEY, generateUserId);
    private readonly fingerprint: string = this.getOrCreateStorageValue(CHATBOT_FINGERPRINT_KEY, generateFingerprint);

    // ICD-10 Code patterns
    private readonly PATTERNS = {
        // Disease: A00.0, A00.00 (with decimal)
        disease: /\b([A-Z]\d{2}\.\d{1,2})\b/g,
        // Type: A00, B99 (3 chars, no decimal, no range)
        type: /\b([A-Z]\d{2})(?!\.\d)(?!-)\b/g,
        // Section: A00-A09, B99-B99
        section: /\b([A-Z]\d{2}-[A-Z]\d{2})\b/g,
        // Chapter: Roman numerals I-XXII
        chapter: /\b(I{1,3}|IV|VI{0,3}|IX|X{1,3}I{0,3}|XIV|XV|XVI{0,3}|XIX|XX|XXI|XXII)\b/g,
    };

    // Cache for code resolution
    private codeCache = new Map<string, IcdCodeInfo>();

    // Questions forwarded from manual search (no results) to the assistant
    private pendingQuestionSubject = new BehaviorSubject<AssistantQuestion | null>(null);
    public pendingQuestion$ = this.pendingQuestionSubject.asObservable();
    private openRequestSubject = new Subject<string>();
    public openRequest$ = this.openRequestSubject.asObservable();
    private hostBotUuids: string[] = [];

    constructor(
        private http: HttpClient,
        private oauthService?: SenseSphereOAuthService
    ) {}

    private getDeviceInfo(): any {
        return {
            user_agent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            screen: (typeof screen !== 'undefined' ? { width: screen.width, height: screen.height } : null)
        };
    }

    private get storage(): Storage | null {
        return typeof window !== 'undefined' ? window.localStorage : null;
    }

    private getOrCreateStorageValue(key: string, factory: () => string): string {
        const storage = this.storage;
        if (!storage) {
            return factory();
        }

        const existingValue = storage.getItem(key);
        if (existingValue) {
            return existingValue;
        }

        const newValue = factory();
        storage.setItem(key, newValue);
        return newValue;
    }

    private get deviceId(): string | null {
        return this.storage?.getItem(CHATBOT_DEVICE_ID_KEY) || null;
    }

    private saveDeviceId(deviceId: string | undefined): void {
        if (deviceId) {
            this.storage?.setItem(CHATBOT_DEVICE_ID_KEY, deviceId);
        }
    }

    hasSession(): boolean {
        return !!this.currentSessionUuid;
    }

    private get sessionStorageKey(): string {
        return `${CHATBOT_STORAGE_PREFIX}:${this.botUuid}:${CHATBOT_SESSION_KEY_SUFFIX}`;
    }

    private loadSessionFromStorage(): void {
        const storage = this.storage;
        if (!storage || !this.botUuid) {
            return;
        }

        const rawSession = storage.getItem(this.sessionStorageKey);
        if (!rawSession) {
            return;
        }

        try {
            const data = JSON.parse(rawSession) as { session_uuid?: string; owner?: string | null };
            this.currentSessionUuid = data.session_uuid || null;
            this.currentSessionOwner = data.owner ?? null;
        } catch {
            this.clearSessionFromStorage();
        }
    }

    private saveSessionToStorage(sessionUuid: string): void {
        const storage = this.storage;
        if (!storage || !this.botUuid) {
            return;
        }

        const owner = this.oauthService?.getUserId() || null;
        storage.setItem(this.sessionStorageKey, JSON.stringify({ session_uuid: sessionUuid, owner }));
        this.currentSessionUuid = sessionUuid;
        this.currentSessionOwner = owner;
    }

    private clearSessionFromStorage(): void {
        const storage = this.storage;
        if (storage && this.botUuid) {
            storage.removeItem(this.sessionStorageKey);
        }
        this.currentSessionUuid = null;
        this.currentSessionOwner = null;
    }

    resumeSession(): Observable<ChatSession> {
        return this.initSession(true);
    }

    /**
     * Initialize a new chat session
     */
    initSession(includeMessages = true, forceNew = false): Observable<ChatSession> {
        const sphereToken = this.oauthService?.getToken();
        const userUuid = this.oauthService?.getUserId();

        // A session from before login/logout belongs to another identity; the backend
        // would hand an anonymous session to a logged-in user and then reject its messages.
        if (this.currentSessionUuid && this.currentSessionOwner !== (userUuid || null)) {
            this.clearSessionFromStorage();
        }

        const body: any = {
            include_messages: includeMessages,
            device_info: this.getDeviceInfo()
        };

        if (userUuid) {
            body.user_uuid = userUuid;
        } else {
            body.user_id = this.userId;
        }

        body.fingerprint = this.fingerprint;

        if (this.deviceId) {
            body.device_id = this.deviceId;
        }

        if (this.currentSessionUuid) {
            body.session_uuid = this.currentSessionUuid;
        }

        if (forceNew) {
            body.force_new = true;
        }

        const headers = sphereToken ? { 'X-User-Token': sphereToken } : undefined;
        return this.http.post<any>(`${CHATBOT_API_BASE}/chatbot/${this.botUuid}/init/`, body, { headers }).pipe(
            map((response) => {
                if (response.success) {
                    const session: ChatSession = {
                        session_uuid: response.session_uuid,
                        session_id: response.session_id,
                        api_session_id: response.api_session_id,
                        bot_name: response.bot_name,
                        existing: response.existing,
                        messages: this.parseMessages(response.messages || []),
                        message_count: response.message_count,
                    };

                    this.saveDeviceId(response.device_id);
                    this.sessionSubject.next(session);
                    this.saveSessionToStorage(response.session_uuid);

                    return session;
                } else {
                    throw new Error(response.error || 'Failed to initialize session');
                }
            }),
            catchError((error) => {
                // Expired SenseSphere token: drop it so the retry runs anonymously
                // instead of failing again with the same token.
                if (this.isTokenExpired(error, sphereToken)) {
                    this.handleExpiredToken();
                }

                // If we got 401/403 and haven't forced new yet, clear stale session and retry.
                if (!forceNew && (error?.status === 401 || error?.status === 403)) {
                    this.clearSessionFromStorage();
                    return this.initSession(includeMessages, true);
                }

                throw error;
            })
        );
    }

    /**
     * Force create a new session
     */
    createNewSession(): Observable<ChatSession> {
        const sphereToken = this.oauthService?.getToken();
        const userUuid = this.oauthService?.getUserId();

        const body: any = {
            force_new: true,
            device_info: this.getDeviceInfo()
        };

        if (userUuid) {
            body.user_uuid = userUuid;
        } else {
            body.user_id = this.userId;
        }

        body.fingerprint = this.fingerprint;

        if (this.deviceId) {
            body.device_id = this.deviceId;
        }

        // Clear old session before creating new one
        this.clearSession();

        const headers = sphereToken ? { 'X-User-Token': sphereToken } : undefined;
        return this.http.post<any>(`${CHATBOT_API_BASE}/chatbot/${this.botUuid}/init/`, body, { headers }).pipe(
            map((response) => {
                if (response.success) {
                    const session: ChatSession = {
                        session_uuid: response.session_uuid,
                        session_id: response.session_id,
                        api_session_id: response.api_session_id,
                        bot_name: response.bot_name,
                        existing: response.existing,
                        messages: this.parseMessages(response.messages || []),
                        message_count: response.message_count,
                    };

                    this.saveDeviceId(response.device_id);
                    this.sessionSubject.next(session);
                    this.saveSessionToStorage(response.session_uuid);

                    return session;
                } else {
                    throw new Error(response.error || 'Failed to create session');
                }
            }),
            catchError((error) => {
                if (this.isTokenExpired(error, sphereToken)) {
                    this.handleExpiredToken();
                }
                return throwError(() => error);
            })
        );
    }

    private parseMessages(messages: any[]): ChatMessage[] {
        return messages.map((m) => ({
            id: m.id?.toString() || Date.now().toString(),
            agent: m.agent || 'user',
            message: m.message || '',
            created_at: m.created_at || new Date().toISOString(),
            order: m.order,
            bot: m.bot,
            bot_name: m.bot_name,
            bot_avatar: m.bot_avatar,
            image_url: m.image_url,
            prompt_tokens: m.prompt_tokens,
            completion_tokens: m.completion_tokens,
            total_tokens: m.total_tokens,
            cached_tokens: m.cached_tokens,
            user_rating: m.user_rating,
            lms_config_name: m.lms_config_name,
        }));
    }

    /**
     * Send a message to the bot
     */
    sendMessage(message: string): Observable<ChatResponse> {
        const currentSession = this.sessionSubject.value;
        if (!currentSession) {
            throw new Error('No active session. Call initSession() first.');
        }

        return this.sendMessageWithRecovery(message, currentSession);
    }

    private sendMessageWithRecovery(message: string, session: ChatSession): Observable<ChatResponse> {
        const sphereToken = this.oauthService?.getToken();
        return this.sendMessageHelper(message, session).pipe(
            catchError((error) => {
                // Don't resend with an expired token (it fails the same way) or silently
                // as anonymous; log out and let the user sign in again.
                if (this.isTokenExpired(error, sphereToken)) {
                    this.handleExpiredToken();
                    return throwError(() => error);
                }

                if (!this.isSessionError(error)) {
                    return throwError(() => error);
                }

                // Reset stale session and retry exactly once with a fresh session.
                return this.createNewSession().pipe(
                    switchMap((newSession) => this.sendMessageHelper(message, newSession))
                );
            })
        );
    }

    private isTokenExpired(error: any, sphereToken: string | null | undefined): boolean {
        return !!sphereToken && error?.status === 401;
    }

    private handleExpiredToken(): void {
        this.oauthService?.logout();
        this.clearSession();
    }

    private isSessionError(error: any): boolean {
        const status = error?.status;
        const rawMessage =
            error?.error?.message ||
            error?.error?.error ||
            error?.message ||
            '';
        const normalizedMessage = String(rawMessage).toLowerCase();

        if (status === 401 || status === 403 || status === 404 || status === 409 || status === 410) {
            return true;
        }

        return normalizedMessage.includes('session') || normalizedMessage.includes('api_session_id');
    }

    private sendMessageHelper(message: string, session: ChatSession): Observable<ChatResponse> {
        const sphereToken = this.oauthService?.getToken();

        const body: any = {
            session_uuid: session.session_uuid,
            message,
            // Lets the backend recognise a resend and lets us poll a pending reply
            request_id: generateFingerprint(),
            device_info: this.getDeviceInfo()
        };

        if (!sphereToken) {
            body.fingerprint = this.fingerprint;
            if (this.deviceId) {
                body.device_id = this.deviceId;
            }
        }

        const headers = sphereToken ? { 'X-User-Token': sphereToken } : undefined;
        return this.http.post<any>(`${CHATBOT_API_BASE}/chatbot/${this.botUuid}/chat/`, body, { headers }).pipe(
            // A slow or interrupted upstream reply comes back as 202 pending; fetch the real one
            switchMap((response) =>
                response?.pending
                    ? this.pollChatRequest(body.request_id, response.retry_after, Date.now() + CHAT_REQUEST_WAIT_MS)
                    : of(response)
            ),
            map((response) => {
                if (response.success) {
                    // The reply can arrive after the user switched bot or started a new chat;
                    // it belongs to the session it was sent on, not whatever is open now.
                    const currentSession = this.sessionSubject.value;
                    const stillOnSession = currentSession?.session_uuid === session.session_uuid;

                    // Backend may fall back to a new session (also reported by a polled reply,
                    // which has no new_session flag); keep later sends on it
                    const movedToSession =
                        response.session_uuid && response.session_uuid !== session.session_uuid
                            ? String(response.session_uuid)
                            : null;
                    if (stillOnSession && movedToSession) {
                        this.saveSessionToStorage(movedToSession);
                    }

                    // Add bot message to session
                    const botMessage: ChatMessage = {
                        id: response.bot_message_id?.toString() || Date.now().toString(),
                        agent: 'bot',
                        message: response.response_html || response.response || '',
                        created_at: response.timestamp || new Date().toISOString(),
                        total_tokens: response.total_tokens,
                        prompt_tokens: response.prompt_tokens,
                        completion_tokens: response.completion_tokens,
                        cached_tokens: response.cached_tokens,
                    };

                    if (currentSession && stillOnSession) {
                        const updatedSession: ChatSession = {
                            ...currentSession,
                            ...(movedToSession ? { session_uuid: movedToSession } : {}),
                            messages: [...currentSession.messages, botMessage],
                            message_count: currentSession.message_count + 2,
                        };
                        this.sessionSubject.next(updatedSession);
                    }

                    return {
                        success: true,
                        response: response.response,
                        user_message_id: response.user_message_id?.toString(),
                        bot_message_id: response.bot_message_id?.toString(),
                        timestamp: response.timestamp,
                    };
                } else {
                    throw new Error(response.error || 'Failed to send message');
                }
            })
        );
    }

    private pollChatRequest(requestId: string, retryAfter: number | undefined, deadline: number): Observable<any> {
        const sphereToken = this.oauthService?.getToken();
        const headers = sphereToken ? { 'X-User-Token': sphereToken } : undefined;
        const params: Record<string, string> = { fingerprint: this.fingerprint };
        if (this.deviceId) {
            params['device_id'] = this.deviceId;
        }

        const waitSeconds = Math.max(1, Number(retryAfter) || 2);
        const delay = Math.min(waitSeconds * 1000, Math.max(0, deadline - Date.now()));

        return timer(delay).pipe(
            switchMap(() =>
                this.http.get<any>(
                    `${CHATBOT_API_BASE}/chatbot/${this.botUuid}/chat-requests/${requestId}/`,
                    { params, headers, observe: 'response' }
                ).pipe(
                    catchError((error) => {
                        // Transient failures don't mean the reply failed; keep waiting
                        if (error?.status === 0 || error?.status === 429 || error?.status >= 500) {
                            return of(null);
                        }
                        // Keep 401 so the caller can handle an expired token
                        if (error?.status === 401) {
                            return throwError(() => error);
                        }
                        // Not a session problem: the message was accepted, so it must not be resent
                        return throwError(() => new Error(CHAT_REQUEST_FAILED_MESSAGE));
                    })
                )
            ),
            switchMap((res: HttpResponse<any> | null) => {
                const data = res?.body;
                if (!res || res.status === 202 || data?.pending) {
                    if (Date.now() >= deadline) {
                        return throwError(() => new Error(CHAT_REQUEST_SLOW_MESSAGE));
                    }
                    const nextWait = res
                        ? Math.max(Number(data?.retry_after) || 2, Math.min(15, waitSeconds * 1.5))
                        : Math.min(30, waitSeconds * 2);
                    return this.pollChatRequest(requestId, nextWait, deadline);
                }
                if (!data?.success) {
                    return throwError(() => new Error(CHAT_REQUEST_FAILED_MESSAGE));
                }
                return of(data);
            })
        );
    }

    /**
     * Add user message to session locally
     */
    addUserMessage(message: string): void {
        const currentSession = this.sessionSubject.value;
        if (currentSession) {
            const userMessage: ChatMessage = {
                id: Date.now().toString(),
                agent: 'user',
                message,
                created_at: new Date().toISOString(),
            };

            const updatedSession: ChatSession = {
                ...currentSession,
                messages: [...currentSession.messages, userMessage],
            };
            this.sessionSubject.next(updatedSession);
        }
    }

    /**
     * Get current session
     */
    getCurrentSession(): ChatSession | null {
        return this.sessionSubject.value;
    }

    /**
     * Clear session and start fresh
     */
    clearSession(): void {
        this.sessionSubject.next(null);
        this.clearSessionFromStorage();
    }

    /**
     * Rate a bot message (1-5 stars)
     * Endpoint: PATCH /api/chat/sessions/{id}/update_message_feedback/?message_id={msg_uuid}
     */
    rateMessage(sessionId: number, messageId: string, rating: number): Observable<FeedbackResponse> {
        const sphereToken = this.oauthService?.getToken();
        const headers = sphereToken ? { 'X-User-Token': sphereToken } : undefined;

        const body: any = { user_rating: rating };
        if (!sphereToken) {
            // Anonymous ownership is checked by device_id (the cookie isn't sent cross-origin)
            body.fingerprint = this.fingerprint;
            if (this.deviceId) {
                body.device_id = this.deviceId;
            }
        }

        return this.http.patch<any>(
            `${CHATBOT_API_BASE}/api/chat/sessions/${sessionId}/update_message_feedback/`,
            body,
            { params: { message_id: messageId }, headers }
        ).pipe(
            map(response => response as FeedbackResponse),
            catchError(error => {
                if (this.isTokenExpired(error, sphereToken)) {
                    this.handleExpiredToken();
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Get usage stats (includes bonus_messages_remaining)
     * Endpoint: GET /api/chat/usage/?bot_uuid={uuid}
     */
    getUsageStats(): Observable<any> {
        const sphereToken = this.oauthService?.getToken();
        const headers = sphereToken ? { 'X-User-Token': sphereToken } : undefined;

        return this.http.get<any>(`${CHATBOT_API_BASE}/api/chat/usage/`, {
            params: this.botUuid ? { bot_uuid: this.botUuid } : {},
            headers
        }).pipe(
            catchError(() => of(null))
        );
    }

    /**
     * Update a specific message's feedback in the current session
     */
    updateMessageFeedback(sessionId: number, messageId: string, feedback: any): void {
        const currentSession = this.sessionSubject.value;
        if (!currentSession) return;

        const updatedMessages = currentSession.messages.map(m =>
            m.id === messageId ? { ...m, user_feedback: feedback } : m
        );

        this.sessionSubject.next({
            ...currentSession,
            messages: updatedMessages
        });
    }

    /**
     * Register a page-level mini chatbot that can be opened for the given bot
     */
    registerHost(botUuid: string): void {
        this.hostBotUuids.push(botUuid);
    }

    unregisterHost(botUuid: string): void {
        const index = this.hostBotUuids.indexOf(botUuid);
        if (index > -1) {
            this.hostBotUuids.splice(index, 1);
        }
    }

    /**
     * Open the mini chatbot of the current page, if it has one.
     * Returns false when the page has none (caller should navigate to the full chatbot page).
     */
    openHostAssistant(): boolean {
        const botUuid = this.hostBotUuids[this.hostBotUuids.length - 1];
        if (!botUuid) {
            return false;
        }
        this.openRequestSubject.next(botUuid);
        return true;
    }

    /**
     * Queue a question for the assistant and ask the mini chatbot on the current page to open.
     * Returns false when no mini chatbot is available for this bot (caller should navigate to the full chatbot page).
     */
    askAssistant(botUuid: string, question: string): boolean {
        this.pendingQuestionSubject.next({ botUuid, question, createdAt: Date.now() });
        if (this.hostBotUuids.includes(botUuid)) {
            this.openRequestSubject.next(botUuid);
            return true;
        }
        return false;
    }

    consumePendingQuestion(botUuid: string): string | null {
        const pending = this.pendingQuestionSubject.value;
        if (!pending || pending.botUuid !== botUuid) {
            return null;
        }
        this.pendingQuestionSubject.next(null);
        return Date.now() - pending.createdAt <= PENDING_QUESTION_TTL_MS ? pending.question : null;
    }
}
